/**
 * Coordinating hook for AssetDrawer data fetching
 *
 * Consolidates 4 separate queries (assets, attributes, riskCounts, technologiesTree)
 * into a single interface while maintaining React Query's automatic parallelism.
 * Implements proper dependency management for riskCounts query which requires entityGroup.
 *
 * OSINT partition is handled automatically via OsintContext — when the drawer
 * is wrapped in OsintProvider, all useMy calls use the OSINT partition.
 *
 * Note: Risk counts use useCounts (/my/count) because there are no per-asset
 * statistics in the backend. Statistics only exist at the global level.
 * The drawer context (single asset detail) is different from table loads.
 */

import { useMemo } from 'react';

import { useCounts } from '@/hooks/useCounts';
import { useMy } from '@/hooks/useMy';
import { useOsintContext } from '@/state/OsintContext';
import { Asset, Attribute, Statistics, TreeNode } from '@/types';

export interface UseAssetDrawerDataResult {
  /** Main asset data array (typically single asset) */
  assets: Asset[];
  /** Asset attributes from graph query */
  assetAttributes: Attribute[];
  /** Risk counts from /my/count API - status codes mapped to counts (e.g., OC: 5, OH: 10) */
  riskCounts: Statistics | undefined;
  /** Technologies tree structure from graph query */
  technologiesTree: TreeNode[];
  /** Combined asset object with attributes attached */
  asset: Asset;
  /** Entity group extracted from first asset */
  entityGroup: string;
  /** Entity identifier extracted from first asset */
  entityIdentifier: string;
  /** True if any query is in initial loading state */
  isInitialLoading: boolean;
  /** True if any query is loading (including refetches) */
  isLoading: boolean;
  /** Individual query statuses for granular control */
  statuses: {
    assets: 'pending' | 'error' | 'success';
    attributes: 'pending' | 'error' | 'success';
    riskCounts: 'pending' | 'error' | 'success';
    technologies: 'pending' | 'error' | 'success';
  };
}

/**
 * Custom hook for coordinating AssetDrawer data queries
 *
 * Executes 4 queries in optimal order:
 * 1. assets, attributes, technologies - execute in parallel (all need assetKey)
 * 2. riskCounts - executes after assets loads (needs entityGroup from assets[0])
 *
 * @param assetKey - The asset key to fetch data for
 * @returns Consolidated data with loading states
 */
export function useAssetDrawerData(assetKey: string): UseAssetDrawerDataResult {
  const { isOsint } = useOsintContext();

  // Query 1: Fetch main asset data
  const { data: assets = [], status: assetsStatus } = useMy(
    {
      resource: 'asset',
      query: {
        node: {
          labels: ['Asset'],
          filters: [
            { field: 'key', operator: '=', value: assetKey || '', not: false },
          ],
        },
      },
    },
    { enabled: Boolean(assetKey) }
  );

  // Query 2: Fetch asset attributes (parallel with assets)
  const { data: assetAttributes = [], status: attributesStatus } = useMy(
    {
      query: {
        node: {
          labels: ['Attribute'],
          relationships: [
            {
              label: 'HAS_ATTRIBUTE',
              source: {
                labels: ['Asset'],
                filters: [
                  { field: 'key', operator: '=', value: assetKey || '' },
                ],
              },
            },
          ],
        },
      },
      resource: 'attribute',
    },
    { enabled: Boolean(assetKey) }
  );

  // Extract entityGroup and entityIdentifier from assets
  const entityGroup = assets[0] ? assets[0].group : '';
  const entityIdentifier = assets[0] ? assets[0].identifier : '';

  // Query 3: Fetch risk counts via /my/count API (dependent on entityGroup from assets)
  // Note: There are no per-asset risk statistics in the backend, so we use useCounts
  // which queries live data filtered by entityGroup. This is acceptable for drawer context
  // (single asset detail view) vs bulk table loads.
  // Disabled for OSINT partition — counts don't exist there.
  const { data: riskCounts, status: riskCountsStatus } = useCounts(
    {
      resource: 'risk',
      query: `#risk#${entityGroup || ''}`,
    },
    {
      // Enable only when we have assetKey AND entityGroup, and NOT osint
      enabled: Boolean(assetKey) && Boolean(entityGroup) && !isOsint,
    }
  );

  // Query 4: Fetch technologies tree (parallel with assets and attributes)
  const { data: technologiesTree = [], status: technologiesStatus } = useMy(
    {
      tree: true,
      query: {
        node: {
          labels: ['Asset'],
          filters: [
            {
              field: 'key',
              operator: '=',
              value: assetKey || '',
            },
          ],
          relationships: [
            {
              label: 'HAS_PORT',
              target: {
                labels: ['Port'],
                relationships: [
                  {
                    label: 'HAS_TECHNOLOGY',
                    optional: true,
                    target: { labels: ['Technology'] },
                  },
                ],
              },
            },
          ],
        },
      },
      resource: 'technology',
    },
    { enabled: Boolean(assetKey) }
  );

  // Combine asset with attributes for components that need it
  // Note: Using full array dependencies as React Compiler prefers simpler inference
  const asset: Asset = useMemo(() => {
    const baseAsset = assets[0] || ({} as Asset);
    return {
      ...baseAsset,
      attributes: assetAttributes,
    };
  }, [assets, assetAttributes]);

  // Calculate unified loading states
  const isInitialLoading =
    assetsStatus === 'pending' ||
    attributesStatus === 'pending' ||
    riskCountsStatus === 'pending';

  const isLoading =
    assetsStatus === 'pending' ||
    attributesStatus === 'pending' ||
    riskCountsStatus === 'pending' ||
    technologiesStatus === 'pending';

  return {
    assets,
    assetAttributes,
    riskCounts,
    technologiesTree,
    asset,
    entityGroup,
    entityIdentifier,
    isInitialLoading,
    isLoading,
    statuses: {
      assets: assetsStatus,
      attributes: attributesStatus,
      riskCounts: riskCountsStatus,
      technologies: technologiesStatus,
    },
  };
}
